import React from "react";
import { Flair } from "tsi-common-react/src/components/Flair";
import { RichNavigationLink } from "tsi-common-react/src/components/RichNavigationLink";
import { ValueProps } from "tsi-common-react/src/components/RichNavigationLinkCollection/index";
import { RichText } from "tsi-common-react/src/components/RichText";
import { SVGIcon } from "tsi-common-react/src/components/SVGIcon/index";
import { t } from "ttag";

import { Context } from "@reactivated";

import { ImageChooserBlock } from "@thelabnyc/thelabui/src/components/ImageChooserBlock";
import { classNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./index.module.scss";

type ISiteHeaderChrome = React.ContextType<
    typeof Context
>["chrome"]["site_header"];

type IRichNavigation = NonNullable<ISiteHeaderChrome["rich_navigation"]>;

// Content-assembly and link-stream shapes, derived from the projected snippet
// rather than re-declared, so a change to the server's _RICH_NAV_PICK or to
// RichNavigationContentAssembly surfaces here as a type error.
type IContentBlock = NonNullable<IRichNavigation["content"]>["value"][number];
type ICollectionValue = Extract<
    IContentBlock,
    { type: "rich_nav_link_collection" }
>["value"];
type IStandardLinkBlock = Extract<IContentBlock, { type: "link" }>;
type ILinkBlock = NonNullable<
    NonNullable<ICollectionValue["links"]>[number]
>["value"][number];

// Promo-period gating, matching the shared link components: a link with no
// period, or with an active one, renders. Every RichNavigationLinkTypes member
// carries `promo_period`, so this reads off the union directly.
const isPromoActive = (link: ILinkBlock): boolean =>
    !link.value.promo_period || !!link.value.promo_period.is_promo_active;

// The one arrow glyph used by tiles, rows and CTAs alike. `.arrow` carries its
// sizing and `fill: currentcolor`; each context adds only its own spacing.
const TileArrow = () => (
    <SVGIcon
        name="arrowRight"
        svgClassName={styles.arrow}
        alt={t`arrow right icon`}
    />
);

// An image-led link: image, optional flair overlay, rich-text label. Shared by
// the general / image / offer / product / dynamic-product link types, which
// differ only in which extra fields they carry.
const RichNavTile = (props: {
    href: string | undefined;
    image?: React.ComponentProps<typeof ImageChooserBlock>["value"] | null;
    flair?: React.ComponentProps<typeof Flair>["flair"] | null;
    secondaryFlair?: React.ComponentProps<typeof Flair>["flair"] | null;
    promoBubbleText?: string | null;
    title?: string | null;
    text?: string | null;
    subtitle?: string | null;
    callout?: string | null;
}) => (
    <a className={styles.tile} href={props.href}>
        {props.image && (
            <ImageChooserBlock
                attrs={{ className: styles.tileImg }}
                value={props.image}
            />
        )}
        {props.flair && (
            <span className={styles.tileFlair}>
                <Flair flair={props.flair} />
            </span>
        )}
        {props.secondaryFlair && (
            <span className={styles.tileFlairSecondary}>
                <Flair flair={props.secondaryFlair} />
            </span>
        )}
        {props.promoBubbleText && (
            <span className={styles.promoBubble}>{props.promoBubbleText}</span>
        )}
        {props.title && <RichText html={props.title} />}
        <span className={styles.tileLabel}>
            {props.text && <RichText html={props.text} />}
            <TileArrow />
        </span>
        {props.subtitle && (
            <span className={styles.tileSubtitle}>
                <RichText html={props.subtitle} />
            </span>
        )}
        {props.callout && (
            <span className={styles.tileCallout}>
                <RichText html={props.callout} />
            </span>
        )}
    </a>
);

// A text-only link row, for the link types that carry no image.
const RichNavRow = (props: {
    href: string | undefined;
    text: string | null | undefined;
    isButton?: boolean;
}) => (
    <a
        className={classNames([
            [styles.row, true],
            [styles.rowButton, !!props.isButton],
        ])}
        href={props.href}
    >
        <span className={styles.rowLabel}>
            {props.text && <RichText html={props.text} />}
        </span>
        <TileArrow />
    </a>
);

// One entry from a collection's `links` list. Every RichNavigationLinkTypes
// member is handled: image-led types render as tiles, text-only types as rows.
const RichNavLinkBlock = ({ link }: { link: ILinkBlock }) => {
    switch (link.type) {
        case "rich_nav_general_link":
            return (
                <RichNavTile
                    href={link.value.destination?.url}
                    image={link.value.image}
                    flair={link.value.flair}
                    text={link.value.text}
                />
            );
        case "rich_nav_image_link":
            return (
                <RichNavTile
                    href={link.value.destination?.url}
                    image={link.value.image}
                    text={link.value.text}
                />
            );
        case "rich_nav_banner_link":
            return (
                <RichNavTile
                    href={link.value.destination?.url}
                    flair={link.value.flair}
                    text={link.value.text}
                />
            );
        case "rich_nav_offer_link":
            // `offer` is this type's destination field; every other tile type
            // calls it `destination`.
            return (
                <RichNavTile
                    href={link.value.offer?.url}
                    image={link.value.image}
                    flair={link.value.flair}
                    text={link.value.text}
                />
            );
        case "rich_nav_product_link":
            return (
                <RichNavTile
                    href={link.value.destination?.url}
                    image={link.value.image}
                    flair={link.value.flair}
                    promoBubbleText={link.value.promo_bubble?.text}
                    text={link.value.text}
                    subtitle={link.value.subtitle}
                    callout={link.value.callout}
                />
            );
        case "rich_nav_dynamic_product_link":
            // NOTE: this block's serializer attaches a `price` payload
            // (PriceSerializer, see _RichNavigation__DynamicProductLink) that
            // the tile does not render -- S&F has no desktop price treatment
            // yet. Everything else on the block renders.
            return (
                <RichNavTile
                    href={link.value.destination?.url}
                    image={link.value.image}
                    flair={link.value.flair}
                    secondaryFlair={link.value.secondary_flair}
                    title={link.value.title}
                    text={link.value.text}
                />
            );
        case "rich_nav_button_link":
            return (
                <RichNavRow
                    href={link.value.destination?.url}
                    text={link.value.text}
                    isButton
                />
            );
        case "rich_nav_callout_link":
            // Callout's destination field is `link`, and its text is optional.
            return (
                <RichNavRow
                    href={link.value.link?.url}
                    text={link.value.text}
                />
            );
        default:
            return null;
    }
};

// A `rich_nav_link_collection`: its links in authored order, plus the optional
// CTA.
//
// The CTA is independent of the links: a collection whose every promo period
// has expired, or one authored with a CTA and no links at all, still renders
// its "Shop all"-style CTA. That matches the shared
// RichNavigationLinkCollectionContent the tempurpedic desktop nav uses, which
// computes emptiness from links AND the CTA rather than from surviving links
// alone -- so promo-period rotation behaves the same on both brands.
// FIELDS THIS DELIBERATELY IGNORES. The shared RichNavigationLinkCollection
// StructBlock also carries `name`, `collection_name_style`, `variant` and
// `promo_features`, none of which S&F's flat desktop treatment reads:
//
//   - `name` / `collection_name_style`: the tempurpedic desktop nav uses the
//     collection name as a dropdown trigger label. S&F has no trigger row --
//     collections render flat, matching the existing S&F slide-out nav -- so
//     there is nothing for the name to label.
//   - `variant`: the RichNavigationVariantOptions values are CSS class names
//     applied by the shared RichNavigationLinkCollection wrapper, which this
//     component does not mount. Selecting "Grid Promo Layout" (or any other
//     variant) therefore has no effect here.
//   - `promo_features`: Image Promos are laid out by that same wrapper, beside
//     the link grid. They are dropped here.
//
// Ticket #31526's configuration instructions are site-agnostic and tell editors
// to set the Grid Promo Layout variant and author Image Promos, so an S&F
// editor following them will see no change. Narrowing the desktop palette to
// the fields each brand actually supports is tracked as a follow-up; until then
// this list is the only record of the divergence.
const RichNavCollection = ({ value }: { value: ICollectionValue }) => {
    const links = (value.links ?? [])
        .flatMap((link) => link?.value ?? [])
        .filter(isPromoActive);
    const hasCta = !!(value.cta_link && value.cta_text);
    if (links.length === 0 && !hasCta) {
        return null;
    }
    return (
        <section className={styles.collection}>
            {links.length > 0 && (
                <div className={styles.items}>
                    {links.map((link, i) => (
                        <RichNavLinkBlock key={i} link={link} />
                    ))}
                </div>
            )}
            {value.cta_link && value.cta_text && (
                <a className={styles.cta} href={value.cta_link.url}>
                    {value.cta_text}
                    <TileArrow />
                </a>
            )}
        </section>
    );
};

// A top-level `link` block: the standard-menu entry (spec: "Standard menus such
// as 'Offers' are built using a Wagtail Link"). The <span> wrapper exists only
// to scope the row styles: RichNavigationLink owns its own <a> and accepts no
// className. See `.standardLink` in index.module.scss for the styling
// rationale.
const RichNavStandardLink = ({
    value,
}: {
    value: IStandardLinkBlock["value"];
}) => (
    <span className={styles.standardLink}>
        <RichNavigationLink {...value} />
    </span>
);

const isStandardLink = (block: IContentBlock): block is IStandardLinkBlock =>
    block.type === "link";

// The two block groups are split out of the content stream rather than rendered
// in authored order, because the design fixes their positions: cards left, link
// list right. Within each group authored order is preserved.
export const DesktopRichNav = ({
    desktop_rich_navigation,
}: {
    desktop_rich_navigation: IRichNavigation;
}) => {
    const blocks = desktop_rich_navigation.content?.value ?? [];
    const valueProps = desktop_rich_navigation.value_props ?? [];
    const standardLinks = blocks.filter(isStandardLink);
    const mainBlocks = blocks.filter((block) => !isStandardLink(block));

    return (
        <nav className={styles.desktopRichNav}>
            <div className={styles.main}>
                {mainBlocks.map((block, blockIndex) => {
                    switch (block.type) {
                        case "rich_nav_link_collection":
                            return (
                                <RichNavCollection
                                    key={blockIndex}
                                    value={block.value}
                                />
                            );
                        case "rich_nav_collection_container":
                            return (
                                <section
                                    key={blockIndex}
                                    className={styles.container}
                                >
                                    {block.value.title && (
                                        <h2 className={styles.containerTitle}>
                                            {block.value.title}
                                        </h2>
                                    )}
                                    {(block.value.collections ?? []).map(
                                        (collection, i) => (
                                            <RichNavCollection
                                                key={i}
                                                value={collection}
                                            />
                                        ),
                                    )}
                                    {block.value.cta && (
                                        <RichNavStandardLink
                                            value={block.value.cta}
                                        />
                                    )}
                                </section>
                            );
                        default:
                            return null;
                    }
                })}
            </div>

            {standardLinks.length > 0 && (
                <div className={styles.linkColumn}>
                    {standardLinks.map((block, i) => (
                        <RichNavStandardLink key={i} value={block.value} />
                    ))}
                </div>
            )}

            {valueProps.length > 0 && (
                <div className={styles.valueProps}>
                    <ValueProps value_props={valueProps} />
                </div>
            )}
        </nav>
    );
};
